/**
 * Configuration and derivation for the sidebar's "Related Resources" block:
 * hand-curated external links shown for the mission(s) the current filters leave
 * in scope.
 *
 * The keys of {@link ADDITIONAL_SOURCES} are opaque, case-sensitive Solr
 * `mission_ss` values — exactly the strings the index returns, which are also the
 * keys of `CONSTANTS.LEGEND_DATES`. A key that matches no indexed mission is
 * silently ignored (no error, no empty block), so a typo just means the links
 * never appear.
 *
 * Watch the spelling: the indexed CryoSat mission is `CryoSat`, NOT `CryoSat-2`,
 * and EarthCARE is `EarthCARE` — the `MISSION` the report ingestion writes into
 * `mission_ss`.
 *
 * Every mission links its Earth Online mission page; EarthCARE also links its
 * ESA MAAP portal and the portal's Data Access and Quality Control Dashboard.
 */

/** One external link shown under a mission. */
export interface AdditionalSourceLink {
    readonly label: string;
    readonly url: string;
}

/** Mission (`mission_ss` value) → its external links. */
export type AdditionalSourcesConfig = Readonly<Record<string, readonly AdditionalSourceLink[]>>;

/** One rendered block section: a mission subtitle and its links. */
export interface AdditionalSourcesGroup {
    mission: string;
    links: readonly AdditionalSourceLink[];
}

/** The Solr facet field holding the mission. */
export const MISSION_FIELD = 'mission_ss';

/** Label of the Earth Online mission-page link every mission gets. */
export const EARTH_ONLINE_OVERVIEW_LABEL = 'Earth Online – Mission overview';

/** A facet map as the store holds it: field name → available values. */
type FacetValuesMap = Readonly<Record<string, readonly string[]>>;

export const ADDITIONAL_SOURCES: AdditionalSourcesConfig = {
    'ALOS-1': [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/alos',
        },
    ],
    Aeolus: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/aeolus',
        },
    ],
    CryoSat: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/cryosat',
        },
    ],
    'ERS-2': [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/ers',
        },
    ],
    EarthCARE: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/earthcare',
        },
        {
            label: 'ESA MAAP Portal',
            url: 'https://portal.maap.eo.esa.int/earthcare/',
        },
        {
            label: 'Data Access and Quality Control Dashboard',
            url: 'https://portal.maap.eo.esa.int/earthcare/earthcare-qc-timeline-dashboard-atlid/',
        },
    ],
    Envisat: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/envisat',
        },
    ],
    GOCE: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/goce',
        },
    ],
    'Landsat-8': [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/landsat-8',
        },
    ],
    'Proba-V': [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/proba-v',
        },
    ],
    SMOS: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/smos',
        },
    ],
    Swarm: [
        {
            label: EARTH_ONLINE_OVERVIEW_LABEL,
            url: 'https://earth.esa.int/eogateway/missions/swarm',
        },
    ],
};

/**
 * Works out which missions the "Related Resources" block should list.
 *
 * - A mission explicitly selected in the sidebar wins (several may be selected).
 * - Otherwise the block only appears once some other filter has narrowed the set
 *   of still-available missions below the full, unfiltered list.
 *
 * Missions without configured links are dropped, so an empty result means the
 * block must not render at all.
 *
 * The store's own types promise these are always set, but the Facets reducers
 * write `action.payload.facetMap` straight into state, so a reply without that key
 * leaves them undefined — hence the `undefined` in the parameter types, the same
 * case Facets.tsx guards with `currentFacetMap !== undefined`.
 *
 * @param facetSelected - selected filters as `"value@facetName"`
 * @param currentFacetMap - facet values still available under the current filters
 * @param facetMap - all facet values, unfiltered
 * @param config - mission → links; defaults to {@link ADDITIONAL_SOURCES}
 * @returns one group per configured mission, sorted alphabetically
 */
export function selectAdditionalSources(
    facetSelected: readonly string[] | undefined,
    currentFacetMap: FacetValuesMap | undefined,
    facetMap: FacetValuesMap | undefined,
    config: AdditionalSourcesConfig = ADDITIONAL_SOURCES
): AdditionalSourcesGroup[] {
    const selectedMissions = (facetSelected ?? [])
        .filter((entry) => entry.split('@')[1] === MISSION_FIELD)
        .map((entry) => entry.split('@')[0])
        .filter((mission) => mission !== '');

    let missions: readonly string[];

    if (selectedMissions.length > 0) {
        missions = selectedMissions;
    } else {
        const all = facetMap?.[MISSION_FIELD] ?? [];
        const current = currentFacetMap?.[MISSION_FIELD] ?? [];
        // Comparing lengths is enough to answer "did anything narrow the mission
        // list?": getAvailableFacets seeds facetMap and currentFacetMap from the
        // identical payload (Facets_Slice.ts), and every later currentFacetMap is
        // the same list scoped down by the active filters — always a subset. So a
        // shorter current list means narrowed, and an equal one means untouched;
        // no set difference is needed. An empty facetMap means the facets have not
        // loaded yet, which is not narrowing either.
        if (all.length === 0 || current.length >= all.length) {
            return [];
        }
        missions = current;
    }

    const uniqueMissions = Array.from(new Set(missions.filter((mission) => mission !== ''))).sort();

    return uniqueMissions
        .filter(
            // Own-property check on purpose: `mission in config` would resolve a
            // mission called `constructor` (or `toString`) to an inherited Object
            // member and blow up on `.length`.
            (mission) =>
                Object.prototype.hasOwnProperty.call(config, mission) && config[mission].length > 0
        )
        .map((mission) => ({ mission, links: config[mission] }));
}
